/**
 * Dialog "Lançar preço manual" — TH-53224 (M2.E8.S2.2).
 *
 * POST /api/products/:id/prices — backend marca source = `manual:{user_id}`
 * e fecha o preço vigente atual (append-only). promo_price < price é validado
 * server-side; replicamos check no cliente pra UX.
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";

interface Store {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  productId: string;
  stores: Store[];
}

interface CreatePayload {
  store_id: string;
  price: string;
  promo_price?: string;
  valid_from?: string;
  valid_until?: string;
}

async function createPrice(
  productId: string,
  payload: CreatePayload,
): Promise<void> {
  const res = await fetch(`/api/products/${productId}/prices`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

export function NewPriceDialog({
  open,
  onOpenChange,
  productId,
  stores,
}: Props) {
  const queryClient = useQueryClient();

  const [storeId, setStoreId] = useState("");
  const [price, setPrice] = useState("");
  const [promoPrice, setPromoPrice] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStoreId(stores[0]?.id ?? "");
      setPrice("");
      setPromoPrice("");
      setValidFrom("");
      setValidUntil("");
      setError(null);
    }
  }, [open, stores]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!storeId) return Promise.reject(new Error("escolha uma loja"));
      const priceNum = Number(price.replace(",", "."));
      if (!Number.isFinite(priceNum) || priceNum <= 0)
        return Promise.reject(new Error("preço inválido"));

      const payload: CreatePayload = {
        store_id: storeId,
        price: priceNum.toFixed(2),
      };
      if (promoPrice.trim()) {
        const promoNum = Number(promoPrice.replace(",", "."));
        if (!Number.isFinite(promoNum) || promoNum <= 0)
          return Promise.reject(new Error("promo_price inválido"));
        if (promoNum >= priceNum)
          return Promise.reject(
            new Error("promo_price deve ser menor que price"),
          );
        payload.promo_price = promoNum.toFixed(2);
      }
      if (validFrom)
        payload.valid_from = new Date(validFrom).toISOString();
      if (validUntil)
        payload.valid_until = new Date(validUntil).toISOString();

      return createPrice(productId, payload);
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({
        queryKey: ["product-prices", productId],
      });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setError(null);
      }}
    >
      <DialogContent>
        <DialogTitle>Lançar preço manual</DialogTitle>
        <DialogDescription>
          Cria uma nova entrada no histórico de preços. O preço vigente atual
          dessa loja é fechado automaticamente.
        </DialogDescription>

        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="np-store">Loja</Label>
            <Select
              id="np-store"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              required
            >
              <option value="">— selecione —</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="np-price">Preço regular</Label>
              <Input
                id="np-price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputMode="decimal"
                placeholder="0,00"
                required
                className="font-mono"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="np-promo">Preço promocional</Label>
              <Input
                id="np-promo"
                value={promoPrice}
                onChange={(e) => setPromoPrice(e.target.value)}
                inputMode="decimal"
                placeholder="opcional"
                className="font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="np-from">Vigente a partir</Label>
              <Input
                id="np-from"
                type="datetime-local"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="np-until">Vigente até</Label>
              <Input
                id="np-until"
                type="datetime-local"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
          </div>

          {error ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {error}
            </div>
          ) : null}

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !storeId || !price.trim()}
            >
              {mutation.isPending ? "Salvando…" : "Lançar preço"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
